import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  ClipboardText, ListChecks, Plus, Trash, FloppyDisk,
} from "@phosphor-icons/react";
import SectionCard from "@/components/expediente/SectionCard";
import StatusChip from "@/components/expediente/StatusChip";

const GARANTIAS_SUGERIDAS = [
  "Continente",
  "Contenido",
  "RC (Responsabilidad Civil)",
  "Estética",
  "Robo",
  "Otros daños",
];

const fmtEuro = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
};

/** Pill "preliminar" usada como `titleBadge` en los bloques exclusivos
 *  del informe preliminar (#1D4ED8 sobre #EAF1FE). */
const PreliminarBadge = () => (
  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-status-info-bg text-status-info-fg text-[10px] font-semibold uppercase tracking-wide">
    preliminar
  </span>
);

/** Borde lateral izquierdo azul fino — la otra "seña" que indica que el
 *  bloque pertenece al informe preliminar. */
const PRELIM_BORDER_CLS = "border-l-[3px] border-l-status-info-fg";

/** Textarea + auto-guardado para "Gestiones pendientes". */
export function GestionesPendientesBlock({ exp, onReload }) {
  const [value, setValue] = useState(exp.gestiones_pendientes || "");
  const [flashed, setFlashed] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { setValue(exp.gestiones_pendientes || ""); }, [exp.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async (v) => {
    try {
      await api.put(`/expedientes/${exp.id}`, { gestiones_pendientes: v });
      setFlashed(true);
      setTimeout(() => setFlashed(false), 1200);
      onReload?.();
    } catch (e) { /* silencioso */ }
  };

  const onChange = (v) => {
    setValue(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist(v), 1200);
  };

  const filled = value.trim().length > 0;

  return (
    <SectionCard
      icon={ListChecks}
      tone={filled ? "teal" : "gray"}
      title="Gestiones pendientes"
      titleBadge={<PreliminarBadge />}
      className={PRELIM_BORDER_CLS}
      testid="informe-gestiones-pendientes"
      meta={
        <>
          <StatusChip filled={filled} testid="informe-status-gestiones" />
          <span className="tabular-nums text-[11px]">{value.length.toLocaleString("es-ES")} car.</span>
          {flashed && (
            <span className="inline-flex items-center gap-1 text-status-success-fg font-semibold uppercase tracking-wider text-[10px]">
              <FloppyDisk className="w-3 h-3" weight="fill" /> guardado
            </span>
          )}
        </>
      }
    >
      <textarea
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => { if (debounceRef.current) { clearTimeout(debounceRef.current); persist(value); } }}
        data-testid="informe-textarea-gestiones-pendientes"
        placeholder={"Ej.\n1. Recibir presupuesto reparador\n2. Solicitar póliza completa\n3. Visita de seguimiento programada para confirmar daños"}
        className="no-base-input !text-[13px] !leading-relaxed !min-h-[140px] !border !border-border !shadow-none !bg-white !w-full !rounded-md focus:!ring-2 focus:!ring-brand/20 focus:!border-brand !p-2.5"
      />
      <div className="text-[10.5px] text-muted italic mt-2">
        Este bloque destaca de forma visible en el PDF del informe preliminar, en azul, para que la
        aseguradora vea claramente qué queda por hacer.
      </div>
    </SectionCard>
  );
}


/** Tabla editable para reserva preventiva: garantía + importe + notas. */
export function ReservaPreventivaBlock({ exp, onReload }) {
  const [items, setItems] = useState(exp.reserva_preventiva_garantias || []);
  const [flashed, setFlashed] = useState(false);
  const persistTimer = useRef(null);

  useEffect(() => { setItems(exp.reserva_preventiva_garantias || []); }, [exp.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = items.reduce((acc, it) => acc + Number(it.importe || 0), 0);

  const persist = async (next) => {
    try {
      await api.put(`/expedientes/${exp.id}`, { reserva_preventiva_garantias: next });
      setFlashed(true);
      setTimeout(() => setFlashed(false), 1200);
      onReload?.();
    } catch (e) { /* silencioso */ }
  };

  const schedulePersist = (next) => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => persist(next), 1000);
  };

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    setItems(next);
    schedulePersist(next);
  };

  const addItem = (garantia = "") => {
    const next = [...items, { garantia, importe: 0, notas: "" }];
    setItems(next);
    persist(next);
  };

  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    persist(next);
  };

  const filled = items.length > 0;

  return (
    <SectionCard
      icon={ClipboardText}
      tone={filled ? "teal" : "gray"}
      title="Reserva preventiva"
      titleBadge={<PreliminarBadge />}
      className={PRELIM_BORDER_CLS}
      testid="informe-reserva-preventiva"
      meta={
        <>
          <StatusChip filled={filled} testid="informe-status-reserva" />
          <span className="text-[11px] tabular-nums">
            {items.length > 0
              ? `${items.length} línea${items.length === 1 ? "" : "s"} · ${fmtEuro(total)}`
              : "0 líneas"}
          </span>
          {flashed && (
            <span className="inline-flex items-center gap-1 text-status-success-fg font-semibold uppercase tracking-wider text-[10px]">
              <FloppyDisk className="w-3 h-3" weight="fill" /> guardado
            </span>
          )}
        </>
      }
    >
        {items.length === 0 ? (
          <div className="text-[12px] text-muted italic">
            Aún no hay líneas de reserva. Añade una garantía / cobertura con su importe orientativo.
          </div>
        ) : (
          <table className="w-full text-[12px]" data-testid="reserva-preventiva-table">
            <thead className="text-[10px] uppercase tracking-widest font-bold text-muted">
              <tr>
                <th className="text-left py-1">Garantía</th>
                <th className="text-right py-1 w-32">Importe (€)</th>
                <th className="text-left py-1">Notas</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id || `reserva-${idx}`} className="border-t border-border/40" data-testid={`reserva-row-${idx}`}>
                  <td className="py-1 pr-2">
                    <input
                      list="rp-garantias"
                      value={it.garantia || ""}
                      onChange={(e) => updateItem(idx, { garantia: e.target.value })}
                      data-testid={`reserva-garantia-${idx}`}
                      className="!text-[12px] !py-1 w-full"
                      placeholder="Continente / Contenido / RC ..."
                    />
                  </td>
                  <td className="py-1 px-2 text-right">
                    <input
                      type="number"
                      step="0.01"
                      value={it.importe ?? 0}
                      onChange={(e) => updateItem(idx, { importe: parseFloat(e.target.value) || 0 })}
                      data-testid={`reserva-importe-${idx}`}
                      className="!text-[12px] !py-1 w-full !text-right font-mono"
                    />
                  </td>
                  <td className="py-1 px-2">
                    <input
                      value={it.notas || ""}
                      onChange={(e) => updateItem(idx, { notas: e.target.value })}
                      data-testid={`reserva-notas-${idx}`}
                      className="!text-[12px] !py-1 w-full"
                      placeholder="(opcional)"
                    />
                  </td>
                  <td className="py-1 text-right">
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      data-testid={`reserva-remove-${idx}`}
                      className="text-muted hover:text-red-600"
                      title="Eliminar"
                    >
                      <Trash className="w-3.5 h-3.5" weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-blue-200 bg-blue-50/60 font-bold">
                <td className="py-1.5 text-[11.5px] uppercase tracking-wider text-blue-800">Total reserva preventiva</td>
                <td className="py-1.5 px-2 text-right font-mono text-blue-900 text-[13px]" data-testid="reserva-total">
                  {fmtEuro(total)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>
        )}
        <datalist id="rp-garantias">
          {GARANTIAS_SUGERIDAS.map((g) => <option key={g} value={g} />)}
        </datalist>
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {GARANTIAS_SUGERIDAS.filter((g) => !items.some((it) => it.garantia === g)).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => addItem(g)}
              data-testid={`reserva-add-${g.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-blue-200 bg-white text-blue-700 hover:bg-blue-50"
            >
              <Plus className="w-3 h-3" weight="bold" /> {g}
            </button>
          ))}
          <button
            type="button"
            onClick={() => addItem("")}
            data-testid="reserva-add-libre"
            className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border bg-white text-muted hover:text-foreground"
          >
            <Plus className="w-3 h-3" weight="bold" /> garantía libre
          </button>
        </div>
        <div className="text-[10.5px] text-muted italic mt-2">
          Cifras orientativas. La valoración definitiva se establecerá en el informe final una vez completada la tasación.
        </div>
    </SectionCard>
  );
}
